// New-tenant wizard: /tenants/new
//
//   1 Name & subdomain   live availability (GET …/check-subdomain, debounced)
//   2 Sport              the pack that gets activated and bootstrapped
//   3 First admin        an email to invite, or skip
//   4 Plan & trial       trial length, package, add-ons, notes → Create
//
// Create is ONE request (POST /platform/leagues, atomic): league, sport pack
// and trial subscription together, so the tenant works the moment it exists
// (#325, #326). It is sent with `admin: null`; the first admin is then
// invited by email (POST /platform/leagues/:id/invites, role admin), so they
// set their own password (registration plan S2, default 20). A failed invite
// never hides or rolls back the league: the done screen says so and links to
// the People tab. A server error naming a `field` jumps back to the step that
// owns it and shows the message on that field.

import { useEffect, useRef, useState } from 'react'
import { api } from '@/api'
import { ApiError, errorMessage } from '@/api/client'
import { FALLBACK_SPORTS } from '@/api/tenants'
import type { CreateTenantResponse } from '@/api/types'
import type { InviteOutcome } from './wizard/DoneScreen'
import { PageHeader } from '@/components/shell/PageHeader'
import { Card, InlineAlert } from '@/components/ui'
import { CheckIcon } from '@/components/ui/icons'
import { cn } from '@/lib/format'
import { useDocumentTitle } from '@/lib/hooks'
import { invalidateQueries, useQuery } from '@/lib/query'
import { STEPS, adminEmailError, draftKeyForField, effectiveSubdomain, emptyDraft, inviteEmail, stepForField, toRequest, trialDaysError, type Draft, type StepIndex } from './wizard/draft'
import { AdminStep, BasicsStep, PlanStep, SportStep, type FieldErrors } from './wizard/Steps'
import { Summary } from './wizard/Summary'
import { DoneScreen } from './wizard/DoneScreen'
import { subdomainOk, useSubdomainCheck } from './wizard/useSubdomainCheck'

const STEP_HINT: Record<StepIndex, string> = {
  0: 'What the league is called and where it lives.',
  1: 'The sport pack to switch on. The tenant admin works the moment it exists.',
  2: 'Who runs the league. We email them an invite; they sign in on the league’s own site.',
  3: 'Every new tenant starts on a trial. Review the summary, then create.',
}

export default function NewTenantPage() {
  useDocumentTitle('New tenant')
  const [step, setStep] = useState<StepIndex>(0)
  const [draft, setDraft] = useState<Draft>(emptyDraft)
  const [errors, setErrors] = useState<FieldErrors>({})
  const [formError, setFormError] = useState<{ title: string; message: string; tone: 'bad' | 'warn' } | null>(null)
  const [pending, setPending] = useState(false)
  const [result, setResult] = useState<CreateTenantResponse | null>(null)
  const [invite, setInvite] = useState<InviteOutcome | null>(null)

  const sub = effectiveSubdomain(draft)
  const check = useSubdomainCheck(sub)
  const sports = useQuery(['sports', 'catalog'], (signal) => api.tenants.sports.catalog(signal).catch(() => FALLBACK_SPORTS))

  // Move focus to the step's heading when the step changes (not on first load).
  const heading = useRef<HTMLHeadingElement>(null)
  const firstRender = useRef(true)
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    heading.current?.focus()
  }, [step])

  const update = (patch: Partial<Draft>) => {
    setDraft((d) => ({ ...d, ...patch }))
    // Editing a field clears its error.
    setErrors((e) => {
      const next = { ...e }
      for (const k of Object.keys(patch) as Array<keyof Draft>) delete next[k]
      if ('subdomainTouched' in patch || 'name' in patch) delete next.subdomain
      return next
    })
  }

  const go = (to: StepIndex) => {
    setFormError(null)
    setStep(to)
  }

  const next = async () => {
    if (step === 0) {
      const e: FieldErrors = {}
      if (!draft.name.trim()) e.name = 'Name is required.'
      if (!sub) e.subdomain = 'Subdomain is required.'
      setErrors(e)
      if (Object.keys(e).length || !subdomainOk(check)) return
      go(1)
    } else if (step === 1) {
      go(2)
    } else if (step === 2) {
      const emailErr = adminEmailError(draft)
      setErrors(emailErr ? { adminEmail: emailErr } : {})
      if (emailErr) return
      go(3)
    } else {
      const trialErr = trialDaysError(draft.trialDays)
      if (trialErr) {
        setErrors({ trialDays: trialErr })
        return
      }
      await create()
    }
  }

  const create = async () => {
    setPending(true)
    setFormError(null)
    try {
      const r = await api.tenants.create(toRequest(draft))
      invalidateQueries('tenants')
      invalidateQueries('dashboard')
      // The league exists now; the invite is a second step that can fail on
      // its own without hiding the tenant.
      const email = inviteEmail(draft)
      let outcome: InviteOutcome | null = null
      if (email) {
        try {
          outcome = { email, result: await api.invites.inviteToTenant(r.tenant.id, { email, role: 'admin' }) }
        } catch (e) {
          outcome = { email, error: errorMessage(e) }
        }
      }
      setInvite(outcome)
      setResult(r)
    } catch (err) {
      if (err instanceof ApiError && err.field && stepForField(err.field) !== null) {
        const key = draftKeyForField(err.field)
        const to = stepForField(err.field)!
        if (key) setErrors({ [key]: err.message })
        else setFormError({ title: "Couldn't create the tenant", message: err.message, tone: 'bad' })
        go(to)
      } else {
        setFormError({ title: "Couldn't create the tenant", message: `${errorMessage(err)} Nothing was created.`, tone: 'bad' })
      }
    } finally {
      setPending(false)
    }
  }

  if (result) {
    return (
      <div className="grid grid-cols-1 gap-5">
        <PageHeader crumbs={[{ label: 'Tenants', to: '/tenants' }, { label: 'New tenant' }]} title="New tenant" />
        <DoneScreen
          result={result}
          invite={invite}
          onAnother={() => {
            setResult(null)
            setInvite(null)
            setDraft(emptyDraft())
            setErrors({})
            setStep(0)
          }}
        />
      </div>
    )
  }

  const stepProps = { draft, update, errors, onNext: next, onBack: step > 0 ? () => go((step - 1) as StepIndex) : undefined }

  return (
    <div className="grid grid-cols-1 gap-5">
      <PageHeader
        crumbs={[{ label: 'Tenants', to: '/tenants' }, { label: 'New tenant' }]}
        title="New tenant"
        description="Creates a league that works straight away: sport enabled, trial started, first admin invited by email."
      />

      <nav aria-label="Wizard steps">
        <ol className="flex flex-wrap gap-x-5 gap-y-2">
          {STEPS.map((label, i) => {
            const done = i < step
            const current = i === step
            const dot = (
              <span
                aria-hidden
                className={cn(
                  'flex h-6 w-6 shrink-0 items-center justify-center rounded-full font-mono text-2xs font-semibold',
                  current ? 'bg-accent-solid text-white' : done ? 'bg-good-soft text-good' : 'bg-raised text-muted ring-1 ring-line',
                )}
              >
                {done ? <CheckIcon size={13} /> : i + 1}
              </span>
            )
            return (
              <li key={label} aria-current={current ? 'step' : undefined} className="flex items-center">
                {done ? (
                  <button type="button" onClick={() => go(i as StepIndex)} className="flex items-center gap-2 rounded-md text-[13px] font-semibold text-muted hover:text-ink">
                    {dot}
                    <span>
                      {label}
                      <span className="sr-only"> (done, go back)</span>
                    </span>
                  </button>
                ) : (
                  <span className={cn('flex items-center gap-2 text-[13px] font-semibold', current ? 'text-ink' : 'text-subtle')}>
                    {dot}
                    {label}
                  </span>
                )}
              </li>
            )
          })}
        </ol>
      </nav>

      <div className="grid items-start gap-4 lg:grid-cols-[1.4fr_1fr]">
        <Card>
          <header className="mb-4">
            <p className="font-mono text-2xs font-semibold uppercase tracking-label text-subtle">
              Step {step + 1} of {STEPS.length}
            </p>
            <h2 ref={heading} id="wizard-step" tabIndex={-1} className="mt-1 text-2xl font-extrabold leading-none text-ink focus:outline-none">
              {STEPS[step]}
            </h2>
            <p className="mt-1.5 text-[13px] text-muted">{STEP_HINT[step]}</p>
          </header>
          {formError ? (
            <InlineAlert tone={formError.tone} title={formError.title} className="mb-4">
              {formError.message}
            </InlineAlert>
          ) : null}
          {step === 0 ? <BasicsStep {...stepProps} check={check} canContinue={subdomainOk(check)} /> : null}
          {step === 1 ? <SportStep {...stepProps} sports={sports.data} /> : null}
          {step === 2 ? <AdminStep {...stepProps} /> : null}
          {step === 3 ? <PlanStep {...stepProps} pending={pending} /> : null}
        </Card>
        <Summary draft={draft} sports={sports.data} />
      </div>
    </div>
  )
}
